"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { buildWorkSignupHref } from "@/lib/signup-next";

/* ─── Season icon (inline with text) ─── */

function SeasonIcon({ src, alt }: { src: string; alt: string }) {
  return (
    <Image
      src={src}
      alt={alt}
      width={20}
      height={20}
      sizes="20px"
      className="inline-block align-middle -mt-0.5 mx-0.5"
    />
  );
}

/* ─── SVG illustrations ─── */

function ProfileIllustration() {
  return (
    <svg
      viewBox="0 0 320 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      aria-hidden="true"
    >
      {/* Card */}
      <rect x="50" y="20" width="220" height="220" rx="18" fill="#FAF7F2" />
      <rect
        x="50"
        y="20"
        width="220"
        height="220"
        rx="18"
        stroke="#1B2840"
        strokeWidth="1.5"
        opacity="0.12"
      />
      {/* Avatar */}
      <circle cx="160" cy="78" r="32" fill="#1C8A99" opacity="0.15" />
      <circle cx="160" cy="68" r="13" fill="#1B2840" opacity="0.7" />
      <ellipse cx="160" cy="95" rx="18" ry="10" fill="#1B2840" opacity="0.7" />
      {/* Form lines */}
      <rect x="95" y="125" width="130" height="8" rx="4" fill="#1B2840" opacity="0.1" />
      <rect x="95" y="147" width="95" height="8" rx="4" fill="#1B2840" opacity="0.1" />
      <rect x="95" y="169" width="110" height="8" rx="4" fill="#1B2840" opacity="0.1" />
      {/* Gold accent bar */}
      <rect x="95" y="193" width="75" height="24" rx="8" fill="#1C8A99" opacity="0.18" />
      <rect x="105" y="200" width="55" height="10" rx="5" fill="#1C8A99" opacity="0.3" />
      {/* Dots */}
      <circle cx="35" cy="55" r="6" fill="#1C8A99" opacity="0.25" />
      <circle cx="290" cy="200" r="8" fill="#1C8A99" opacity="0.15" />
      <circle cx="280" cy="40" r="4" fill="#1B2840" opacity="0.08" />
    </svg>
  );
}

function DiscoverIllustration() {
  return (
    <svg
      viewBox="0 0 320 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      aria-hidden="true"
    >
      {/* Stacked cards */}
      <rect x="110" y="35" width="160" height="110" rx="14" fill="#FAF7F2" stroke="#1B2840" strokeWidth="1" opacity="0.4" />
      <rect x="75" y="50" width="160" height="110" rx="14" fill="#FAF7F2" stroke="#1B2840" strokeWidth="1.2" opacity="0.6" />
      <rect x="40" y="65" width="160" height="110" rx="14" fill="white" stroke="#1B2840" strokeWidth="1.5" opacity="0.9" />
      {/* Front card content */}
      <circle cx="78" cy="103" r="16" fill="#1C8A99" opacity="0.15" />
      <circle cx="78" cy="97" r="7" fill="#1B2840" opacity="0.6" />
      <ellipse cx="78" cy="112" rx="10" ry="5" fill="#1B2840" opacity="0.6" />
      <rect x="105" y="95" width="70" height="6" rx="3" fill="#1B2840" opacity="0.12" />
      <rect x="105" y="110" width="50" height="6" rx="3" fill="#1B2840" opacity="0.08" />
      <rect x="105" y="125" width="65" height="6" rx="3" fill="#1C8A99" opacity="0.15" />
      {/* Magnifying glass */}
      <circle cx="235" cy="185" r="28" stroke="#1C8A99" strokeWidth="2.5" fill="#1C8A99" fillOpacity="0.06" />
      <line x1="255" y1="206" x2="278" y2="229" stroke="#1C8A99" strokeWidth="3" strokeLinecap="round" />
      {/* Sparkles */}
      <circle cx="25" cy="140" r="5" fill="#1C8A99" opacity="0.25" />
      <circle cx="295" cy="55" r="4" fill="#1C8A99" opacity="0.18" />
      <circle cx="255" cy="145" r="3" fill="#1B2840" opacity="0.08" />
    </svg>
  );
}

function LandRoleIllustration() {
  return (
    <svg
      viewBox="0 0 320 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      aria-hidden="true"
    >
      {/* Mountains */}
      <path d="M0 220 L90 90 L130 150 L200 60 L280 180 L320 140 L320 260 L0 260 Z" fill="#1B2840" opacity="0.05" />
      <path d="M0 240 L70 130 L110 170 L180 80 L260 200 L320 160 L320 260 L0 260 Z" fill="#1B2840" opacity="0.04" />
      {/* Check circle */}
      <circle cx="160" cy="115" r="50" fill="#1C8A99" opacity="0.1" />
      <circle cx="160" cy="115" r="38" fill="white" stroke="#1C8A99" strokeWidth="2.5" />
      <path d="M140 115 L153 128 L180 101" stroke="#1C8A99" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Celebration dots */}
      <circle cx="65" cy="50" r="6" fill="#1C8A99" opacity="0.35" />
      <circle cx="255" cy="42" r="5" fill="#1B2840" opacity="0.12" />
      <circle cx="270" cy="85" r="3.5" fill="#1C8A99" opacity="0.25" />
      <circle cx="50" cy="100" r="3" fill="#1C8A99" opacity="0.2" />
      <rect x="100" y="40" width="9" height="9" rx="2" fill="#1C8A99" opacity="0.18" transform="rotate(20 104 44)" />
      <rect x="220" y="55" width="7" height="7" rx="2" fill="#1B2840" opacity="0.08" transform="rotate(-15 223 58)" />
      {/* Wave */}
      <path d="M0 225 Q80 205 160 225 Q240 245 320 225 L320 260 L0 260 Z" fill="#1C8A99" opacity="0.08" />
    </svg>
  );
}

function BrowseListingIllustration() {
  return (
    <svg
      viewBox="0 0 320 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      aria-hidden="true"
    >
      {/* Listing card */}
      <rect x="55" y="25" width="210" height="140" rx="16" fill="white" stroke="#1B2840" strokeWidth="1.5" opacity="0.9" />
      <rect x="80" y="50" width="110" height="8" rx="4" fill="#1B2840" opacity="0.15" />
      <rect x="80" y="68" width="75" height="6" rx="3" fill="#1B2840" opacity="0.08" />
      <rect x="80" y="86" width="155" height="5" rx="2.5" fill="#1B2840" opacity="0.06" />
      <rect x="80" y="97" width="155" height="5" rx="2.5" fill="#1B2840" opacity="0.06" />
      <rect x="80" y="108" width="120" height="5" rx="2.5" fill="#1B2840" opacity="0.06" />
      {/* Button on card */}
      <rect x="80" y="125" width="80" height="24" rx="8" fill="#1C8A99" opacity="0.15" />
      <rect x="92" y="132" width="56" height="10" rx="5" fill="#1C8A99" opacity="0.3" />
      {/* Mini profile cards */}
      <rect x="55" y="182" width="60" height="55" rx="10" fill="#FAF7F2" stroke="#1B2840" strokeWidth="1" opacity="0.7" />
      <circle cx="85" cy="200" r="9" fill="#1B2840" opacity="0.12" />
      <rect x="130" y="182" width="60" height="55" rx="10" fill="#FAF7F2" stroke="#1B2840" strokeWidth="1" opacity="0.7" />
      <circle cx="160" cy="200" r="9" fill="#1B2840" opacity="0.12" />
      <rect x="205" y="182" width="60" height="55" rx="10" fill="#FAF7F2" stroke="#1B2840" strokeWidth="1.2" opacity="0.8" />
      <circle cx="235" cy="200" r="9" fill="#1C8A99" opacity="0.2" />
      {/* Dots */}
      <circle cx="30" cy="100" r="5" fill="#1C8A99" opacity="0.2" />
      <circle cx="290" cy="170" r="4" fill="#1C8A99" opacity="0.15" />
    </svg>
  );
}

function MessageIllustration() {
  return (
    <svg
      viewBox="0 0 320 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      aria-hidden="true"
    >
      {/* Bubble 1 (left) */}
      <rect x="35" y="35" width="155" height="55" rx="14" fill="#1B2840" opacity="0.06" />
      <rect x="55" y="52" width="90" height="6" rx="3" fill="#1B2840" opacity="0.12" />
      <rect x="55" y="66" width="55" height="6" rx="3" fill="#1B2840" opacity="0.08" />
      <path d="M60 90 L78 90 L52 108" fill="#1B2840" opacity="0.06" />
      {/* Bubble 2 (right) */}
      <rect x="130" y="115" width="155" height="55" rx="14" fill="#1C8A99" opacity="0.1" />
      <rect x="150" y="132" width="100" height="6" rx="3" fill="#1C8A99" opacity="0.2" />
      <rect x="150" y="146" width="65" height="6" rx="3" fill="#1C8A99" opacity="0.15" />
      <path d="M260 170 L242 170 L268 188" fill="#1C8A99" opacity="0.1" />
      {/* Bubble 3 (left) */}
      <rect x="35" y="195" width="130" height="45" rx="14" fill="#1B2840" opacity="0.06" />
      <rect x="55" y="212" width="75" height="6" rx="3" fill="#1B2840" opacity="0.12" />
      <path d="M60 240 L78 240 L52 255" fill="#1B2840" opacity="0.06" />
      {/* Dots */}
      <circle cx="300" cy="50" r="5" fill="#1C8A99" opacity="0.25" />
      <circle cx="20" cy="170" r="4" fill="#1C8A99" opacity="0.18" />
    </svg>
  );
}

function HireFreeIllustration() {
  return (
    <svg
      viewBox="0 0 320 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      aria-hidden="true"
    >
      {/* Central circle */}
      <circle cx="160" cy="110" r="58" fill="#1C8A99" opacity="0.08" />
      <circle cx="160" cy="110" r="42" fill="white" stroke="#1C8A99" strokeWidth="2.5" />
      {/* Handshake / meeting symbol */}
      <path d="M132 110 Q142 92 160 105 Q178 92 188 110" stroke="#1B2840" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.7" />
      <path d="M132 110 Q142 128 160 115 Q178 128 188 110" stroke="#1C8A99" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      {/* Badge */}
      <rect x="120" y="175" width="80" height="30" rx="15" fill="#1C8A99" opacity="0.12" />
      <rect x="137" y="184" width="46" height="12" rx="6" fill="#1C8A99" opacity="0.25" />
      {/* Crossed-out cost symbols */}
      <circle cx="60" cy="60" r="17" stroke="#1B2840" strokeWidth="1.2" fill="none" opacity="0.08" />
      <line x1="48" y1="48" x2="72" y2="72" stroke="#1B2840" strokeWidth="1.2" opacity="0.12" />
      <circle cx="260" cy="65" r="17" stroke="#1B2840" strokeWidth="1.2" fill="none" opacity="0.08" />
      <line x1="248" y1="53" x2="272" y2="77" stroke="#1B2840" strokeWidth="1.2" opacity="0.12" />
      {/* Celebration dots */}
      <circle cx="50" cy="155" r="5" fill="#1C8A99" opacity="0.25" />
      <circle cx="270" cy="150" r="4" fill="#1C8A99" opacity="0.2" />
      <circle cx="85" cy="200" r="3" fill="#1B2840" opacity="0.06" />
      <circle cx="235" cy="195" r="3.5" fill="#1B2840" opacity="0.06" />
      <rect x="100" y="40" width="8" height="8" rx="2" fill="#1C8A99" opacity="0.15" transform="rotate(20 104 44)" />
      <rect x="215" y="35" width="7" height="7" rx="2" fill="#1C8A99" opacity="0.12" transform="rotate(-15 218 38)" />
    </svg>
  );
}

/* ─── Data ─── */

const tabs = ["Find Work", "Find Staff"] as const;
type Tab = (typeof tabs)[number];

interface Step {
  title: string;
  desc: ReactNode;
  illustration: ReactNode;
}

const stepsData: Record<Tab, Step[]> = {
  "Find Work": [
    {
      title: "Create a profile",
      desc: (
        <>
          Add your experience, availability, and the kind of work you&rsquo;re
          after: <SeasonIcon src="/winter-icon.png" alt="Winter" /> ski seasons,{" "}
          <SeasonIcon src="/festival-icon.png" alt="Festival" /> festival work,{" "}
          <SeasonIcon src="/summer-icon.png" alt="Summer" /> summer jobs, or
          yacht crew. Takes two minutes. 100% free.
        </>
      ),
      illustration: <ProfileIllustration />,
    },
    {
      title: "Get discovered and browse jobs",
      desc: "Employers browse profiles and reach out to you directly. You can also browse jobs and apply in a click, express interest, or message employers yourself. No lengthy applications.",
      illustration: <DiscoverIllustration />,
    },
    {
      title: "Land your season",
      desc: "Message back, arrange interviews, and secure your next role. Whether it\u2019s a chalet in the Alps, a bar at Glastonbury, or a yacht in the Med.",
      illustration: <LandRoleIllustration />,
    },
  ],
  "Find Staff": [
    {
      title: "Browse candidates or post a listing",
      desc: "Filter candidates by role, season, location and availability. Or post a job listing and let candidates come to you. Browse without signing up, no commitment required.",
      illustration: <BrowseListingIllustration />,
    },
    {
      title: "Message directly",
      desc: "Found someone great? Create a free employer account, message them directly, or review candidates who\u2019ve expressed interest in your listing. No middlemen, no agencies.",
      illustration: <MessageIllustration />,
    },
    {
      title: "Hire for free",
      desc: "No placement fees, no subscriptions, no hidden costs. PeakWave is 100% free for everyone. Now and always.",
      illustration: <HireFreeIllustration />,
    },
  ],
};

const ctaData: Record<Tab, { label: string; href: string }> = {
  "Find Work": {
    label: "I\u2019m looking for work",
    href: buildWorkSignupHref({ next: "/jobs", source: "how-it-works" }),
  },
  "Find Staff": {
    label: "I\u2019m hiring seasonal staff",
    href: "/employer-signup",
  },
};

const faqs = [
  {
    q: "Is PeakWave really free?",
    a: "Yes, 100%. No placement fees, no subscriptions, no hidden charges. Free for candidates and employers, now and always.",
  },
  {
    q: "Do I need experience to sign up?",
    a: "Not at all. Many employers hire first-timers. Just create a profile and be upfront about your experience level.",
  },
  {
    q: "How quickly do employers respond?",
    a: "Response times vary by employer and season. As soon as an employer messages you, you\u2019ll get an email and can reply in the app.",
  },
];

/* ─── Tabs + Steps ─── */

export function HowItWorksTabs() {
  const [active, setActive] = useState<Tab>("Find Work");
  const [animating, setAnimating] = useState(false);

  function switchTab(tab: Tab) {
    if (tab === active) return;
    setAnimating(true);
    setTimeout(() => {
      setActive(tab);
      setAnimating(false);
    }, 180);
  }

  return (
    <div className="flex flex-col items-center">
      {/* Pill toggle */}
      <div className="inline-flex bg-cream rounded-full p-1 mb-5 md:mb-6">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => switchTab(tab)}
            className={`px-6 py-2.5 rounded-full text-[13px] md:text-[14px] font-semibold transition-all cursor-pointer ${
              active === tab
                ? "bg-navy text-white shadow-sm"
                : "text-navy/60 hover:text-navy"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Steps */}
      <div
        className={`w-full transition-opacity duration-180 ease-out ${
          animating ? "opacity-0" : "opacity-100"
        }`}
      >
        {stepsData[active].map((step, i) => {
          const isReversed = i % 2 === 1;
          return (
            <div
              key={step.title}
              className="max-w-[880px] mx-auto mb-4 md:mb-6 last:mb-0"
            >
              <div
                className={`flex flex-col ${
                  isReversed ? "md:flex-row-reverse" : "md:flex-row"
                } items-center gap-5 md:gap-8`}
              >
                {/* Illustration */}
                <div className="hidden md:flex w-full md:max-w-[200px] md:w-5/12 justify-center">
                  {step.illustration}
                </div>

                {/* Text */}
                <div className="w-full md:w-7/12">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#6B7B8D] text-white flex items-center justify-center text-base md:text-lg font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="pt-1.5 md:pt-2">
                      <h3 className="font-serif text-[20px] md:text-[24px] text-navy leading-tight mb-2 md:mb-3">
                        {step.title}
                      </h3>
                      <p className="text-[12px] text-text-muted leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <Link
        href={ctaData[active].href}
        className={`mt-8 md:mt-10 bg-gold text-white px-9 py-3.5 rounded-lg text-[15px] font-semibold no-underline font-sans transition-all inline-flex items-center gap-1.5 hover:bg-gold/90 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(28, 138, 153,0.25)] ${
          animating ? "opacity-0" : "opacity-100"
        } transition-opacity duration-180`}
      >
        {ctaData[active].label} &rarr;
      </Link>
    </div>
  );
}

/* ─── FAQ ─── */

export function HowItWorksFaq() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="max-w-[560px] mx-auto">
      <h2 className="font-serif text-[24px] md:text-[30px] text-navy text-center mb-5">
        Common questions
      </h2>
      <div className="divide-y divide-navy/10">
        {faqs.map((faq, i) => (
          <button
            key={i}
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full text-left py-5 cursor-pointer"
          >
            <div className="flex items-start justify-between gap-4">
              <span className="text-[15px] md:text-base font-semibold text-navy">
                {faq.q}
              </span>
              <span
                className={`text-gold text-xl leading-none shrink-0 transition-transform duration-200 ${
                  open === i ? "rotate-45" : ""
                }`}
              >
                +
              </span>
            </div>
            <div
              className={`overflow-hidden transition-all duration-200 ${
                open === i ? "max-h-40 mt-2.5" : "max-h-0"
              }`}
            >
              <p className="text-[14px] md:text-[15px] text-text-muted leading-relaxed">
                {faq.a}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
